if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.productPage = document.getElementById(this.dataset.id);
      this.productButton = this.productPage.querySelector('[data-js-product-add-to-cart]');
      this.isSingle = this.hasAttribute('data-single');

      const target = this.dataset.appendTo ? document.querySelector(this.dataset.appendTo) : document.body;
      target.appendChild(this);

      this._initVisibility();
      this._initButtons();
      this._initVariantListeners();
      this.updateStickyOptions();
      this.updateCTA(this.productPage.querySelector('product-variants')?.variant);

      // Combined-listing swaps replace the <product-page> node, orphaning our
      // cached productPage/productButton/product-variants references. Re-resolve
      // and rebind whenever that happens.
      this._onProductPageReplaced = (e) => {
        if (e.detail?.productPageId !== this.dataset.id) return;
        this._rebindToProductPage();
      };
      document.addEventListener('product-page:replaced', this._onProductPageReplaced);
    }

    disconnectedCallback() {
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onProductPageReplaced) {
        document.removeEventListener('product-page:replaced', this._onProductPageReplaced);
      }
      this._observer?.disconnect();
    }

    _rebindToProductPage() {
      const next = document.getElementById(this.dataset.id);
      if (!next) return;
      this.productPage = next;
      this.productButton = this.productPage.querySelector('[data-js-product-add-to-cart]');
      if (!this.productButton) return;

      this._initVisibility();
      this._initButtons();
      this._initVariantListeners();
      this.updateStickyOptions();
      this.updateCTA(this.productPage.querySelector('product-variants')?.variant);

      const newTitle = this.productPage.querySelector('[data-js-title-block] .product-title')?.textContent;
      const titleEl = this.querySelector('[data-js-sticky-title]');
      if (newTitle && titleEl) titleEl.textContent = newTitle;
    }

    _initVisibility() {
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);

      this._onScroll = () => {
        const {top} = this.productButton.getBoundingClientRect();
        const show = top < 0;
        this.classList.toggle('visible', show);
        document.body.classList.toggle('sticky-add-to-cart-opened', show);
      };

      window.addEventListener('scroll', this._onScroll, {passive: true});
      this._onScroll();
    }

    _initButtons() {
      if (this.isSingle) {
        const stickyBtn = this.querySelector('button[data-js-atc]');
        const freshBtn = stickyBtn.cloneNode(true);
        stickyBtn.replaceWith(freshBtn);
        // The cloned node inherits any classes from the previous sticky button
        // (e.g. a stale `working` from a click that happened just before a
        // combined-listing swap). Sync to the current productButton state.
        freshBtn.classList.toggle('working', this.productButton.classList.contains('working'));

        freshBtn.addEventListener('click', () => {
          this.productButton.click();
          freshBtn.classList.add('working');
        });

        this._observer?.disconnect();
        this._observer = new MutationObserver(() => {
          freshBtn.classList.toggle('working', this.productButton.classList.contains('working'));
        });
        this._observer.observe(this.productButton, {attributes: true});
      } else {
        const chooseBtn = this.querySelector('button[data-js-choose]');
        const freshBtn = chooseBtn?.cloneNode(true);
        chooseBtn?.replaceWith(freshBtn);
        if (!freshBtn) return;
        freshBtn.classList.toggle('working', this.productButton.classList.contains('working'));

        // Store original text so we can restore it when no variant is selected
        freshBtn.dataset.chooseText = freshBtn.querySelector('.button__text')?.textContent ?? '';

        freshBtn.addEventListener('click', () => {
          // Use the button's visual state (set by updateCTA) rather than re-reading
          // variant — prevents stale state mismatch on mobile where product-variants
          // may finish initializing after our connectedCallback ran.
          if (!freshBtn.classList.contains('button--outline') && !freshBtn.classList.contains('disabled')) {
            this.productButton.click();
          } else {
            this.productButton.scrollIntoView({behavior: 'smooth', block: 'center'});
          }
        });

        this._observer?.disconnect();
        this._observer = new MutationObserver(() => {
          freshBtn.classList.toggle('working', this.productButton.classList.contains('working'));
        });
        this._observer.observe(this.productButton, {attributes: true});
      }
    }

    updateCTA(variant) {
      if (this.isSingle) return;
      const btn = this.querySelector('button[data-js-choose]');
      if (!btn) return;
      const textEl = btn.querySelector('.button__text');
      if (!textEl) return;

      if (variant) {
        textEl.textContent =
          this.productButton.querySelector('.button__text')?.textContent?.trim() || 'Add to Cart';
        btn.classList.remove('button--outline');
        btn.classList.add('button--solid');
        btn.classList.toggle('disabled', !variant.available);
      } else {
        textEl.textContent = btn.dataset.chooseText || 'Choose Options';
        btn.classList.remove('button--solid', 'disabled');
        btn.classList.add('button--outline');
      }
    }

    _initVariantListeners() {
      const productVariants = this.productPage.querySelector('product-variants');
      productVariants?.addEventListener('variant:changed', () => {
        this.updateStickyOptions();
        this.updateCTA(productVariants.variant);
      });

      // Mobile timing fix: product-variants sets variant synchronously in its
      // constructor but never dispatches variant:changed on initial load. If our
      // connectedCallback ran before product-variants finished upgrading, the initial
      // updateCTA call got undefined. Re-sync after all deferred scripts have run.
      requestAnimationFrame(() => {
        const pv = this.productPage.querySelector('product-variants');
        if (pv?.variant !== undefined) {
          this.updateStickyOptions();
          this.updateCTA(pv.variant);
        }
      });
    }

    updateStickyOptions() {
      const optionsEl = this.querySelector('[data-js-sticky-options]');
      const variants = this.productPage.querySelectorAll('product-variants [data-js-product-variant]');
      if (!optionsEl) return;
      if (!variants.length) {
        optionsEl.innerHTML = '';
        return;
      }

      optionsEl.innerHTML = '';
      for (const root of variants) {
        const legacyLabel = root.querySelector('[data-js-sticky-option-name]');
        let name = legacyLabel?.getAttribute('data-js-sticky-option-name')?.trim() ?? '';
        if (!name && legacyLabel?.textContent) {
          name = legacyLabel.textContent.replace(/\s+/g, ' ').trim();
        }
        if (!name) {
          const optionNameEl = root.querySelector('.option__name');
          if (optionNameEl) {
            name = optionNameEl.textContent.replace(/\s+/g, ' ').trim();
          }
        }

        const value =
          root.querySelector('[data-js-current-selection]')?.textContent?.replace(/\s+/g, ' ').trim() ??
          root.querySelector('.product-variant__name + span')?.textContent?.replace(/\s+/g, ' ').trim() ??
          '';

        const line = [name, value].filter(Boolean).join(' ').trim();
        if (!line) continue;

        const row = document.createElement('div');
        row.className = 'sticky-add-to-cart__option';
        row.textContent = line;
        optionsEl.appendChild(row);
      }
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
